import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { RoutingDocumentTypeModal } from '../components/RoutingDocumentTypeModal';
import { routingDocumentTypesApi } from '../routingApi';
import { ROUTING_SETTINGS_PATH } from '../routingRules';
import type { RoutingDocumentTypeListItem } from '../routingTypes';

/**
 * What a routed document is — reached through Routing › Settings, never as a sidebar row of its
 * own. One catalog for both registers: a letter is a letter on the way in and on the way out.
 */
export function RoutingDocumentTypesIndexPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [editing, setEditing] = useState<RoutingDocumentTypeListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{ kind: 'delete' | 'restore'; type: RoutingDocumentTypeListItem } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchTypes = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      routingDocumentTypesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (type: RoutingDocumentTypeListItem): MenuItem[] => [
      {
        key: `restore-${type.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'routing-document-types.delete',
        disabledReason: type.isActive ? 'This document type is already active.' : null,
        onClick: () => setConfirming({ kind: 'restore', type }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'delete') await routingDocumentTypesApi.remove(confirming.type.id);
      else await routingDocumentTypesApi.restore(confirming.type.id);

      toast.success(`${confirming.kind === 'delete' ? 'Deleted' : 'Restored'} ${confirming.type.name}.`);
      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  const filed = confirming ? confirming.type.receivedCount + confirming.type.dispatchedCount : 0;

  return (
    <>
      <IndexPage<RoutingDocumentTypeListItem>
        module="routing-document-types"
        moduleNoun="document types"
        breadcrumb={['Document Management', 'Routing', { label: 'Settings', to: ROUTING_SETTINGS_PATH }, 'Document Types']}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['RoutingDocumentType']}
        rowKey={(type) => type.id}
        fetch={fetchTypes}
        reloadKey={reloadKey}
        searchPlaceholder="Search document types"
        emptyMessage="No document types yet. Add the kinds of paper the organization receives and sends — Letter, Memorandum, Endorsement."
        onCreate={() => setCreating(true)}
        createPermission="routing-document-types.create"
        onRowOpen={(type) => navigate(`${ROUTING_SETTINGS_PATH}/document-types/${type.id}`)}
        openPermission="routing-document-types.read"
        rowActions={rowActions}
        onEdit={(type) => setEditing(type)}
        editPermission="routing-document-types.update"
        onDelete={(type) => setConfirming({ kind: 'delete', type })}
        deletePermission="routing-document-types.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(type) => (type.isActive ? null : 'This document type is already deleted.')}
        onClearData={() => routingDocumentTypesApi.clear()}
        clearDataPermission="routing-document-types.clear-data"
        otherInfo={(type) => type}
      />

      {creating || editing !== null ? (
        <RoutingDocumentTypeModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'delete' ? 'Delete document type' : 'Restore document type'}
          danger={confirming.kind === 'delete'}
          requireConfirm={confirming.kind === 'delete'}
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`routing-document-type-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'delete'
                  ? `"${confirming.type.name}" stops being offered on the Receiving and Dispatching forms.`
                  : `"${confirming.type.name}" is offered again on the Receiving and Dispatching forms.`}
              </p>

              {confirming.kind === 'delete' && filed > 0 ? (
                <p>
                  {filed} {filed === 1 ? 'document is' : 'documents are'} filed under it. They keep naming it — deleting
                  a word does not re-file what was recorded — but it cannot be chosen for anything new.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted document type can be restored from this list.</p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<RoutingDocumentTypeListItem>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 240,
    flex: true,
    sortable: true,
    render: (type) => <TextCell value={type.name} />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 320,
    summary: true,
    render: (type) => <TextCell value={type.description} />,
  },
  {
    // Correlated counts — not sortable.
    key: 'receivedCount',
    header: 'Received',
    width: 100,
    align: 'right',
    summary: true,
    render: (type) => <TextCell value={String(type.receivedCount)} mono numeric />,
  },
  {
    key: 'dispatchedCount',
    header: 'Dispatched',
    width: 110,
    align: 'right',
    render: (type) => <TextCell value={String(type.dispatchedCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (type) =>
      type.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
